import { create } from 'zustand';
import { auth } from '../lib/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { useStorageStore } from './useStorageStore';
import { useDanceStore } from './useDanceStore';
import { initAnalytics, trackGeneralEvent, trackEvent, getOrCreateAnonymousId } from '../lib/analytics';
import { fetchWithAuth } from '../lib/api';
import { LATEST_TOU_VERSION, LATEST_PRIVACY_VERSION } from '../lib/policyVersions';

interface AuthState {
  user: User | null;
  nickname: string | null;
  loading: boolean;
  error: string | null;
  touUpdateRequired: boolean;
  privacyUpdateRequired: boolean;
  acceptedTouVersion: string | null;
  acceptedPrivacyVersion: string | null;
  acceptedTouAt: string | null;
  acceptedPrivacyAt: string | null;
  checkingTerms: boolean;
  tier: 'anonymous' | 'registered' | 'basic' | 'pro';
  initialize: () => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  fetchTermsStatus: () => Promise<void>;
  acceptLatestTerms: (acceptTou: boolean, acceptPrivacy: boolean) => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateNickname: (nickname: string) => Promise<void>;
  updateTier: (tier: 'registered' | 'basic' | 'pro') => Promise<void>;
}

let isInitialized = false;
export const _resetInitializedForTesting = () => { isInitialized = false; };



export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  nickname: null,
  tier: 'anonymous',
  loading: true,
  error: null,
  touUpdateRequired: false,
  privacyUpdateRequired: false,
  acceptedTouVersion: null,
  acceptedPrivacyVersion: null,
  acceptedTouAt: null,
  acceptedPrivacyAt: null,
  checkingTerms: false,

  initialize: () => {
    if (isInitialized) return;
    isInitialized = true;

    set({ loading: true });

    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          trackGeneralEvent('signed_in', { method: 'google' });
        }
      })
      .catch((error) => {
        console.error('Firebase Redirect Auth Error:', error);
        set({ error: error.message, loading: false });
        trackGeneralEvent('sign_in_failed', { method: 'google', error: error.message });
        useDanceStore.getState().setToastMessage(`Google Sign-In failed: ${error.message}. Please try again.`, 'error');
      });

    try {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            await user.getIdToken();
            initAnalytics(user);
            set({ user });
            // Link invitation code to registered user if not already done
            const invitationCode = localStorage.getItem('choreographer_invitation_code');
            const invitationLinked = localStorage.getItem('choreographer_invitation_linked');
            if (invitationCode && !invitationLinked) {
              const anonymousId = getOrCreateAnonymousId();
              fetchWithAuth('/api/invitations/link', {
                method: 'POST',
                body: JSON.stringify({ anonymousId })
              }).then((res: any) => {
                if (res?.success) {
                  localStorage.setItem('choreographer_invitation_linked', 'true');
                }
              }).catch(err => console.error('Failed to link invitation code:', err));
            }

            // Migrate local guest data to the cloud if present
            const localFolders = localStorage.getItem('choreographer_local_folders');
            const localPoses = localStorage.getItem('choreographer_local_poses');
            const localSequences = localStorage.getItem('choreographer_local_sequences');

            if (localFolders || localPoses || localSequences) {
              try {
                const folders = localFolders ? JSON.parse(localFolders) : [];
                const poses = localPoses ? JSON.parse(localPoses) : [];
                const sequences = localSequences ? JSON.parse(localSequences) : [];

                if (folders.length > 0 || poses.length > 0 || sequences.length > 0) {
                  await fetchWithAuth('/api/user/migrate', {
                    method: 'POST',
                    body: JSON.stringify({ folders, poses, sequences })
                  });
                  trackEvent('anon_data_migrated', {
                    folder_count: folders.length,
                    pose_count: poses.length,
                    sequence_count: sequences.length,
                  });
                  console.log('Local guest data migrated successfully');
                }
                localStorage.removeItem('choreographer_local_folders');
                localStorage.removeItem('choreographer_local_poses');
                localStorage.removeItem('choreographer_local_sequences');
              } catch (migrateErr) {
                console.error('Failed to migrate local guest data to cloud:', migrateErr);
                trackEvent('anon_data_migration_failed', { error: (migrateErr as any)?.message || 'unknown' });
              }
            }

            await useStorageStore.getState().loadFromServer();
            await get().fetchTermsStatus();
            await get().fetchProfile();
            
            set({ loading: false });
          } catch (error) {
            console.error('Failed to get user token', error);
            // The restored session could not be verified with the server
            // (e.g. expired/revoked token, network issue). Rather than
            // showing a half-signed-in user with an empty library, land
            // them in the app as a guest, same as if they'd never signed
            // in, and explain what happened via a persistent modal (not a
            // toast, since this is important enough that it shouldn't be
            // missable).
            initAnalytics(null);
            set({
              user: null,
              nickname: null,
              tier: 'anonymous',
              loading: false,
              touUpdateRequired: false,
              privacyUpdateRequired: false,
              acceptedTouVersion: null,
              acceptedPrivacyVersion: null
            });
            useStorageStore.getState().initializeLocal();
            import('../i18n').then(({ default: i18n }) => {
              useDanceStore.getState().confirmAction({
                title: i18n.t('errors.session_restore_failed_title', 'Session Could Not Be Verified'),
                message: i18n.t('errors.session_restore_failed_message', "We couldn't verify your saved sign-in with the server, so you're continuing as a guest. Your cloud library (folders, poses, and sequences) is not available right now and any work you do will be saved only on this device. Please sign in again to restore access to your cloud library."),
                isAlert: true,
                confirmLabel: i18n.t('errors.continue_as_guest', 'Continue as Guest'),
                onConfirm: () => {},
              });
            }).catch(() => {});
          }
        } else {
          initAnalytics(null);
          set({
            user: null,
            nickname: null,
            tier: 'anonymous',
            loading: false,
            touUpdateRequired: false,
            privacyUpdateRequired: false,
            acceptedTouVersion: null,
            acceptedPrivacyVersion: null
          });
          useStorageStore.getState().initializeLocal();
        }
      }, (error) => {
        set({ error: error.message, loading: false });
      });
    } catch (err: any) {
      console.error('Firebase Auth initialization failed:', err);
      set({ error: err?.message || 'Firebase Auth failed to load', loading: false });
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      await signInWithEmailAndPassword(auth, email, password);
      trackGeneralEvent('signed_in', { method: 'email' });
    } catch (err: any) {
      set({ error: err.message, loading: false });
      trackGeneralEvent('sign_in_failed', { method: 'email', error: err.message });
      throw err;
    }
  },

  signup: async (email, password) => {
    set({ loading: true, error: null });
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      trackGeneralEvent('signed_in', { method: 'email_signup' });

      try {
        await fetchWithAuth('/api/user/terms', {
          method: 'POST',
          body: JSON.stringify({ acceptTou: true, acceptPrivacy: true })
        });
        set({
          acceptedTouVersion: LATEST_TOU_VERSION,
          acceptedPrivacyVersion: LATEST_PRIVACY_VERSION,
          touUpdateRequired: false,
          privacyUpdateRequired: false
        });
      } catch (postErr) {
        console.error('Failed to auto-accept terms on signup:', postErr);
      }
    } catch (err: any) {
      set({ error: err.message, loading: false });
      trackGeneralEvent('sign_in_failed', { method: 'email_signup', error: err.message });
      throw err;
    }
  },

  loginWithGoogle: async () => {
    set({ loading: true, error: null });
    try {
      const { default: i18n } = await import('../i18n');
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ hl: i18n.language });
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        await signInWithRedirect(auth, provider);
      } else {
        await signInWithPopup(auth, provider);
        trackGeneralEvent('signed_in', { method: 'google' });
      }
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        set({ loading: false });
        trackEvent('auth_popup_cancelled', { method: 'google' });
        throw err;
      }
      set({ error: err.message, loading: false });
      trackGeneralEvent('sign_in_failed', { method: 'google', error: err.message });
      throw err;
    }
  },

  logout: async () => {
    set({ loading: true, error: null });
    try {
      await signOut(auth);
      trackGeneralEvent('signed_out');
      localStorage.removeItem('choreographer_invitation_code');
      localStorage.setItem('choreographer_invitation_linked', 'false');
    } catch (err: any) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  clearError: () => set({ error: null }),

  fetchTermsStatus: async () => {
    set({ checkingTerms: true });
    try {
      const data = await fetchWithAuth('/api/user/terms');
      const touVersion = data?.acceptedTouVersion || null;
      const privacyVersion = data?.acceptedPrivacyVersion || null;
      const touAt = data?.acceptedTouAt || null;
      const privacyAt = data?.acceptedPrivacyAt || null;

      const current = get();
      const effectiveTouVersion = current.acceptedTouVersion === LATEST_TOU_VERSION
        ? LATEST_TOU_VERSION : touVersion;
      const effectivePrivacyVersion = current.acceptedPrivacyVersion === LATEST_PRIVACY_VERSION
        ? LATEST_PRIVACY_VERSION : privacyVersion;

      set({
        acceptedTouVersion: effectiveTouVersion,
        acceptedPrivacyVersion: effectivePrivacyVersion,
        acceptedTouAt: touAt,
        acceptedPrivacyAt: privacyAt,
        touUpdateRequired: effectiveTouVersion !== LATEST_TOU_VERSION,
        privacyUpdateRequired: effectivePrivacyVersion !== LATEST_PRIVACY_VERSION,
        checkingTerms: false
      });
    } catch (err: any) {
      console.error('Failed to fetch terms status:', err);
      set({ checkingTerms: false });
    }
  },

  acceptLatestTerms: async (acceptTou: boolean, acceptPrivacy: boolean) => {
    try {
      const res = await fetchWithAuth('/api/user/terms', {
        method: 'POST',
        body: JSON.stringify({ acceptTou, acceptPrivacy })
      });

      const updates: any = {};
      if (acceptTou) {
        updates.acceptedTouVersion = LATEST_TOU_VERSION;
        updates.acceptedTouAt = res?.acceptedTouAt || new Date().toISOString();
        updates.touUpdateRequired = false;
      }
      if (acceptPrivacy) {
        updates.acceptedPrivacyVersion = LATEST_PRIVACY_VERSION;
        updates.acceptedPrivacyAt = res?.acceptedPrivacyAt || new Date().toISOString();
        updates.privacyUpdateRequired = false;
      }
      set(updates);
    } catch (err: any) {
      console.error('Failed to accept latest terms:', err);
      throw err;
    }
  },

  fetchProfile: async () => {
    try {
      const data = await fetchWithAuth('/api/user/profile');
      set({ 
        nickname: data?.nickname || null,
        tier: data?.tier || 'registered'
      });
      if (data?.preferences) {
        useStorageStore.setState((state) => ({
          preferences: { ...state.preferences, ...data.preferences }
        }));
      }
    } catch (err) {
      console.error('Failed to fetch user profile data:', err);
    }
  },

  updateNickname: async (nickname: string) => {
    try {
      const res = await fetchWithAuth('/api/user/profile', {
        method: 'POST',
        body: JSON.stringify({ nickname })
      });
      if (res?.success) {
        set({ nickname });
        const currentUser = auth.currentUser;
        if (currentUser) {
          await currentUser.reload();
          set({ user: auth.currentUser });
        }
      }
    } catch (err: any) {
      console.error('Failed to update user nickname:', err);
      throw err;
    }
  },

  updateTier: async (tier: 'registered' | 'basic' | 'pro') => {
    try {
      const res = await fetchWithAuth('/api/user/profile', {
        method: 'POST',
        body: JSON.stringify({ tier })
      });
      if (res?.success) {
        set({ tier });
      }
    } catch (err: any) {
      console.error('Failed to update user tier:', err);
      throw err;
    }
  }
}));
